import React, { useState, useCallback, useRef, useEffect } from 'react';
import './SalesEntryForm.css';
import SalesLookup from './SalesLookup';
import SalesExport from './SalesExport';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const CUSTOMER_TYPES = ['B2C', 'B2B'];
const SALE_STATUSES = ['PAID', 'PARTIAL', 'OWING'];
const SALE_CHANNELS = ['SHOP', 'FAIR', 'WHOLESALE', 'ONLINE', 'DIRECT', 'PARTNER'];

const TODAY = new Date().toISOString().split('T')[0];

const createEmptyItem = () => ({
  productId: '',
  quantity: 1,
  salePrice: '',
  unitPrice: '',
  note: '',
});

function SalesEntryForm({ products = [], productsLoading = false }) {
  // Sub-view toggle
  const [subView, setSubView] = useState('new');

  // Customer state
  const [customerType, setCustomerType] = useState('B2C');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');

  // Order-level state
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [saleChannel, setSalesChannel] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState([createEmptyItem()]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editGroupRef, setEditGroupRef] = useState(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Customer typeahead state
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const fetchSuggestions = useCallback((query, custType) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query, customer_type: custType });
        const res = await fetch(`${API_URL}/sales/customers/suggest?${params}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.customers || []);
          setShowSuggestions((data.customers || []).length > 0);
        }
      } catch {
        // silently ignore – typeahead is best-effort
      }
    }, 300);
  }, []);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer.customer_id);
    if (customerType === 'B2C') {
      setFirstName(customer.first_name || '');
      setLastName(customer.last_name || '');
    } else {
      setBusinessName(customer.name || '');
    }
    setPhone(customer.phone || '');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleNameBlur = () => {
    // Delay hiding so a click on a suggestion can register first
    blurTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleNameFocus = (e) => {
    // If a customer was previously selected, select all text so user can replace it
    if (selectedCustomerId) {
      e.target.select();
      setLastName('');
      setPhone('');
      setSelectedCustomerId(null);
    }
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleLastNameChange = (value) => {
    setLastName(value);
    setSelectedCustomerId(null);
    fetchSuggestions(value, 'B2C');
  };

  const handleFirstNameChange = (value) => {
    setFirstName(value);
    setSelectedCustomerId(null);
    fetchSuggestions(value, 'B2C');
  };

  const handleBusinessNameChange = (value) => {
    setBusinessName(value);
    setSelectedCustomerId(null);
    fetchSuggestions(value, customerType);
  };

  const updateLineItem = useCallback((index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-fill salePrice and unitPrice when product is selected
      if (field === 'productId' && value) {
        const selected = products.find(p => p.product_id === value);
        if (selected && selected.base_unit_price) {
          updated[index].salePrice = selected.base_unit_price.toString();
          updated[index].unitPrice = selected.base_unit_price.toString();
        }
      }

      return updated;
    });
  }, [products]);

  const addLineItem = () => {
    setLineItems(prev => [...prev, createEmptyItem()]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const getLineTotal = (item) => {
    const qty = parseFloat(item.quantity);
    const price = parseFloat(item.salePrice);
    if (qty && price) return qty * price;
    return 0;
  };

  const orderTotal = lineItems.reduce((sum, item) => sum + getLineTotal(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    const payload = {
      customer_id: selectedCustomerId || null,
      customer_type: customerType,
      first_name: customerType === 'B2C' ? firstName : null,
      last_name: customerType === 'B2C' ? lastName : null,
      business_name: customerType !== 'B2C' ? businessName : null,
      phone: phone || null,
      sale_date: saleDate,
      status: status || null,
      amount_paid: status === 'PARTIAL' && amountPaid ? parseFloat(amountPaid) : null,
      sale_channel: saleChannel || null,
      items: lineItems.map(item => ({
        product_id: item.productId,
        quantity: parseInt(item.quantity),
        unit_price: parseFloat(item.unitPrice),
        sale_price: parseFloat(item.salePrice),
        note: item.note || null,
      })),
    };

    try {
      const url = isEditMode
        ? `${API_URL}/sales/entry/${encodeURIComponent(editGroupRef)}`
        : `${API_URL}/sales/entry`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const wasEditing = isEditMode;
        const verb = wasEditing ? 'updated' : 'created';
        setSubmitStatus({
          type: 'success',
          message: `${data.sale_ids.length} sale(s) ${verb}! Order total: ${data.order_total?.toFixed(2)}`,
        });
        resetForm();
        if (wasEditing) {
          // Switch back to find view after a short delay so user sees success message
          setTimeout(() => setSubView('find'), 1500);
        }
      } else {
        const error = await response.json();
        setSubmitStatus({
          type: 'error',
          message: `Failed: ${error.detail || 'Unknown error'}`,
        });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: `Error: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    // Keep customerType and saleDate for rapid repeated entry
    setFirstName('');
    setLastName('');
    setBusinessName('');
    setPhone('');
    setSelectedCustomerId(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setLineItems([createEmptyItem()]);
    setStatus('');
    setAmountPaid('');
    setSalesChannel('');
    setIsEditMode(false);
    setEditGroupRef(null);
  };

  const cancelEdit = () => {
    resetForm();
    setSubView('find');
  };

  const handleEditOrder = (order) => {
    // Pre-populate customer fields
    const custType = order.customer_type || 'B2C';
    setCustomerType(custType);
    setSelectedCustomerId(order.customer_id || null);
    if (custType === 'B2C') {
      setFirstName(order.customer_first_name || '');
      setLastName(order.customer_last_name || '');
      setBusinessName('');
    } else {
      setBusinessName(order.customer_name || '');
      setFirstName('');
      setLastName('');
    }
    setPhone(order.customer_phone || '');

    // Pre-populate order-level fields
    setSaleDate(order.sale_date);
    setStatus(order.status || '');
    setAmountPaid(order.amount_paid != null ? String(order.amount_paid) : '');
    setSalesChannel(order.sale_channel || '');

    // Pre-populate line items
    const items = order.items.map(item => ({
      productId: item.product_id || '',
      quantity: item.quantity,
      salePrice: (item.sale_price ?? item.unit_price).toString(),
      unitPrice: item.unit_price.toString(),
      note: item.note || '',
    }));
    setLineItems(items.length > 0 ? items : [createEmptyItem()]);

    // Enter edit mode
    setIsEditMode(true);
    setEditGroupRef(order.group_ref);
    setSubmitStatus(null);
    setSubView('new');
  };

  if (subView === 'find') {
    return (
      <div className="sales-entry-container">
        <div className="sales-entry-card">
          <div className="sub-view-toggle">
            <button
              type="button"
              className={`sub-view-btn ${subView === 'new' ? 'active' : ''}`}
              onClick={() => setSubView('new')}
            >
              New Sale
            </button>
            <button
              type="button"
              className={`sub-view-btn ${subView === 'find' ? 'active' : ''}`}
              onClick={() => setSubView('find')}
            >
              Find Sale
            </button>
            <button
              type="button"
              className={`sub-view-btn ${subView === 'export' ? 'active' : ''}`}
              onClick={() => setSubView('export')}
            >
              Export
            </button>
          </div>
          <SalesLookup onEditOrder={handleEditOrder} />
        </div>
      </div>
    );
  }

  if (subView === 'export') {
    return (
      <div className="sales-entry-container">
        <div className="sales-entry-card">
          <div className="sub-view-toggle">
            <button
              type="button"
              className={`sub-view-btn ${subView === 'new' ? 'active' : ''}`}
              onClick={() => setSubView('new')}
            >
              New Sale
            </button>
            <button
              type="button"
              className={`sub-view-btn ${subView === 'find' ? 'active' : ''}`}
              onClick={() => setSubView('find')}
            >
              Find Sale
            </button>
            <button
              type="button"
              className={`sub-view-btn ${subView === 'export' ? 'active' : ''}`}
              onClick={() => setSubView('export')}
            >
              Export
            </button>
          </div>
          <SalesExport />
        </div>
      </div>
    );
  }

  return (
    <div className="sales-entry-container">
      <div className="sales-entry-card">
        <div className="sub-view-toggle">
          <button
            type="button"
            className={`sub-view-btn ${subView === 'new' ? 'active' : ''}`}
            onClick={() => setSubView('new')}
          >
            New Sale
          </button>
          <button
            type="button"
            className={`sub-view-btn ${subView === 'find' ? 'active' : ''}`}
            onClick={() => setSubView('find')}
          >
            Find Sale
          </button>
          <button
            type="button"
            className={`sub-view-btn ${subView === 'export' ? 'active' : ''}`}
            onClick={() => setSubView('export')}
          >
            Export
          </button>
        </div>

        <h2>{isEditMode ? 'Edit Sale' : 'New Sale Entry'}</h2>
        <p className="sales-entry-subtitle">
          {isEditMode ? 'Modify this order and save changes' : 'Record a new sale transaction'}
        </p>

        {isEditMode && (
          <div className="edit-mode-banner">
            <span>Editing order: {editGroupRef}</span>
            <button type="button" className="cancel-edit-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Customer Section */}
          <div className="form-section">
            <h3>Customer</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Customer Type *</label>
                <div className="type-selector">
                  {CUSTOMER_TYPES.map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`type-btn ${customerType === type ? 'active' : ''}`}
                      onClick={() => setCustomerType(type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {customerType === 'B2C' ? (
              <div className="form-row">
                <div className="form-group suggestions-wrapper">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={e => handleFirstNameChange(e.target.value)}
                    onBlur={handleNameBlur}
                    onFocus={handleNameFocus}
                    required
                    placeholder="First name"
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {suggestions.map(c => (
                        <div
                          key={c.customer_id}
                          className="suggestion-item"
                          onMouseDown={() => handleSelectCustomer(c)}
                        >
                          <span className="suggestion-name">{c.name}</span>
                          {c.phone && <span className="suggestion-phone">{c.phone}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={e => handleLastNameChange(e.target.value)}
                    required
                    placeholder="Last name"
                  />
                </div>
              </div>
            ) : (
              <div className="form-row">
                <div className="form-group full-width suggestions-wrapper">
                  <label htmlFor="businessName">Business Name *</label>
                  <input
                    id="businessName"
                    type="text"
                    value={businessName}
                    onChange={e => handleBusinessNameChange(e.target.value)}
                    onBlur={handleNameBlur}
                    onFocus={handleNameFocus}
                    required
                    placeholder="Business or organization name"
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {suggestions.map(c => (
                        <div
                          key={c.customer_id}
                          className="suggestion-item"
                          onMouseDown={() => handleSelectCustomer(c)}
                        >
                          <span className="suggestion-name">{c.name}</span>
                          {c.phone && <span className="suggestion-phone">{c.phone}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
            </div>
          </div>

          {/* Sale Details Section */}
          <div className="form-section">
            <h3>Sale Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="saleDate">Sale Date *</label>
                <input
                  id="saleDate"
                  type="date"
                  value={saleDate}
                  max={TODAY}
                  onChange={e => setSaleDate(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="line-items-section">
              <div className="line-item-header">
                <span className="li-col-product">Product</span>
                <span className="li-col-qty">Qty</span>
                <span className="li-col-price">Sale Price</span>
                <span className="li-col-total">Total</span>
                <span className="li-col-action"></span>
              </div>

              {lineItems.map((item, index) => (
                <div className="line-item" key={index}>
                  <div className="line-item-main">
                    <div className="li-col-product">
                      <select
                        value={item.productId}
                        onChange={e => updateLineItem(index, 'productId', e.target.value)}
                        required
                        disabled={productsLoading}
                      >
                        <option value="">
                          {productsLoading ? 'Loading...' : 'Select product'}
                        </option>
                        {products.map(p => (
                          <option key={p.product_id} value={p.product_id}>
                            {p.name}{p.size_or_variant ? ` (${p.size_or_variant})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="li-col-qty">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={e => updateLineItem(index, 'quantity', e.target.value)}
                        required
                      />
                    </div>
                    <div className="li-col-price">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={item.salePrice}
                        onChange={e => updateLineItem(index, 'salePrice', e.target.value)}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    <div className="li-col-total">
                      <div className="line-total-display">
                        {getLineTotal(item).toFixed(2)}
                      </div>
                    </div>
                    <div className="li-col-action">
                      {lineItems.length > 1 && (
                        <button
                          type="button"
                          className="remove-item-btn"
                          onClick={() => removeLineItem(index)}
                          title="Remove item"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="line-item-note">
                    <input
                      type="text"
                      value={item.note}
                      onChange={e => updateLineItem(index, 'note', e.target.value)}
                      placeholder="Item note (optional)"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="add-item-btn"
                onClick={addLineItem}
              >
                + Add Item
              </button>
            </div>

            {/* Order Total */}
            <div className="order-total">
              <span className="order-total-label">Order Total</span>
              <span className="order-total-value">{orderTotal.toFixed(2)}</span>
            </div>

            {/* Order-level fields */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Payment Status</label>
                <select
                  id="status"
                  value={status}
                  onChange={e => {
                    setStatus(e.target.value);
                    if (e.target.value !== 'PARTIAL') setAmountPaid('');
                  }}
                >
                  <option value="">-- Select --</option>
                  {SALE_STATUSES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {status === 'PARTIAL' && (
                <div className="form-group">
                  <label htmlFor="amountPaid">Amount Paid *</label>
                  <input
                    id="amountPaid"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amountPaid}
                    onChange={e => setAmountPaid(e.target.value)}
                    required
                    placeholder="0.00"
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="channel">Sales Channel</label>
                <select
                  id="channel"
                  value={saleChannel}
                  onChange={e => setSalesChannel(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {SALE_CHANNELS.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {submitStatus && (
            <div className={`submit-status ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEditMode ? 'Update Sale' : 'Save Sale')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SalesEntryForm;
